import { SessionsSummary } from "../../api/client-sessions-summary";
import { Chip, Box, Tooltip } from "@mui/material";
import { Euro as EuroIcon } from "@mui/icons-material";
import { WarningBoxVisibilityList } from "../../components/warnings/WarningBox";
import { EEG_SERVICES, NFB_SERVICE, PSY_SERVICE } from "../../utils/constants";

type ClientDebtWarningsProps = {
  sessions: SessionsSummary;
};
const ClientDebtWarnings = ({ sessions }: ClientDebtWarningsProps) => {
  if (!sessions) return null;
  const isTheFirstEvaluation =
    sessions.evaluationsDone === 1 &&
    sessions.evaluationsBought === 1 &&
    sessions.totalDebt === 0 &&
    sessions.nfbDone === 0 &&
    sessions.psyDone === 0 &&
    sessions.followUpDone === 0;
  if (isTheFirstEvaluation) {
    return null;
  }

  const serviceDebts = [
    { name: "Neurofeedback", bought: sessions.nfbBought, done: sessions.nfbDone, serviceId: [NFB_SERVICE] },
    { name: "Psicologia", bought: sessions.psyBought, done: sessions.psyDone, serviceId: [PSY_SERVICE] },
    { name: "Avaliações", bought: sessions.evaluationsBought, done: sessions.evaluationsDone, serviceId: EEG_SERVICES },
  ];

  const servicesInDebt = serviceDebts.filter((service) => service.done > service.bought);

  // Helper function to check if treatment is ongoing
  const isTreatmentOngoing = (serviceId: Array<number>) => {
    if (!serviceId || !sessions.activeTreatment) return false;
    const activeTreatmentForService = sessions.activeTreatment.find(
      (treatment) => serviceId.includes(treatment.service)
    );
    return !!activeTreatmentForService;
  };

  

  // Only show pack finished warnings for ongoing treatments
  const servicesPackFinished = serviceDebts.filter(
    (service) =>
      service.bought === service.done &&
      service.bought > 0 &&
      (service.name === "Neurofeedback" || service.name === "Psicologia") &&
      isTreatmentOngoing(service.serviceId)
  );

  return {
    serviceDebt: servicesInDebt.length > 0 ? <ServicesInDebt servicesInDebt={servicesInDebt} /> : null,
    packsFinished:
      servicesPackFinished.length > 0 ? <PacksFinished servicesPackFinished={servicesPackFinished} /> : null,
  };
};



type ServiceInDebt = {
  name: string;
  bought: number;
  done: number;
  serviceId: Array<number>;
};
const ServicesInDebt = ({ servicesInDebt }: { servicesInDebt: Array<ServiceInDebt> }) => {
  const debtChips = (
    <Box sx={{ display: "flex",flex:1, rowGap: 1, columnGap: 2, flexWrap: "wrap", justifyContent: "center" }}>
      {servicesInDebt.map((service) => (
        <Chip
          sx={{ width: "fit-content", px: 1 }}
          key={service.name}
          label={service.name}
          color="error"
          size="small"
          variant="filled"
        />
      ))}
    </Box>
  );

  return (
    <Tooltip title="Sessões em dívida">
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flex: 1,
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <EuroIcon color="error" />
        {debtChips}
      </Box>
    </Tooltip>
  );
};



type ServicesPackFinished = {
  name: string;
  bought: number;
  done: number;
  serviceId: Array<number>;
};
const PacksFinished = ({ servicesPackFinished }: { servicesPackFinished: Array<ServicesPackFinished> }) => {
  const finishedChips = (
    <Box sx={{ display: "flex", flex:1, rowGap: 1, columnGap: 2, flexWrap: "wrap", justifyContent: "center" }}>
      {servicesPackFinished.map((service) => (
        <Chip
          sx={{ width: "fit-content", px: 1 }}
          key={service.name}
          label={service.name}
          color="warning"
          size="small"
          variant="filled"
        />
      ))}
    </Box>
  );
  return (
    <Tooltip
      title={
        <>
          Os Packs de sessões terminaram.
          <br />
          Verificar se o cliente adquiriu novos.
        </>
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flex: 1,
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
      <EuroIcon color="warning" />
        {finishedChips}
      </Box>
    </Tooltip>
  );
};

export const clientDebtWarnings = (sessions: SessionsSummary) => {
  const warnings = ClientDebtWarnings({ sessions });

  const serviceDebt = warnings?.serviceDebt;
  const packsFinished = warnings?.packsFinished;

  const warningVisibleTo: WarningBoxVisibilityList = ["reception"]

  return {
    debtWarning: { show: !!serviceDebt, content: serviceDebt, color: "error", visibleTo: warningVisibleTo },
    packWarning: { show: !!packsFinished, content: packsFinished, color: "warning", visibleTo: warningVisibleTo },
  };
};
